import { Sort } from '@angular/material/sort';

import { SqlAggregateFunctionType } from '@models/helpers';
import { DisplayType, KeyValueFormattingModel, KeyValueRestrictionModel } from '@models/module/fields/enums';
import { AppColors, DropdownItem } from '@shared/consts';

export type ColumnPriorityType = 0 | 1 | 2 | 3;

export interface TableColumn {
  id: number;
  name: string;
  displayName?: string;
  /**
   * Translation key for a screen-reader-only header label. Use when the visible
   * `displayName` is blank or a glyph/icon and the column still needs to be
   * named for assistive tech (WCAG 1.3.1 / axe `empty-table-header`).
   */
  srOnlyLabel?: string;
  align?: 'start' | 'center' | 'end';
  displayFormatId?: DisplayFormats;
  width?: number;
  priority?: ColumnPriorityType;
  sortable?: boolean;
  showThumbnail?: boolean;
  formattings?: KeyValueFormattingModel[];
  restrictions?: KeyValueRestrictionModel[];
  isPrimarySort?: boolean;
  isDescending?: boolean;
  isFilter?: boolean;
  filterOnly?: boolean;
  isContractSystemField?: boolean;
}

export interface TableRow {
  state_name?: StatusColumnType;
  backgroundColor?: AppColors;
  hoverColor?: AppColors;
  image?: {
    type: 'icon' | 'image';
    iconKey?: string;
    class?: string;
    /** Small status glyphs rendered after the image, e.g. a "shared" marker on a file. */
    badges?: {
      iconKey: string;
      class?: string;
      title?: string;
    }[];
  };
  empty?: boolean;
  selectable?: boolean;
  excludeFromCount?: boolean;
  customClass?: string;
  [key: string]: unknown;
}

/**
 * Represents a footer row in a table.
 *
 * @property {number} key - A unique identifier for the footer row.
 * @property {string} label - An optional label for the footer row. This will be placed in the first column, if the table has selectable rows.
 * @property {any} key - Should match the name propery of the column in the table.
 */
export interface FooterRow {
  key: SqlAggregateFunctionType;
  label?: string;
  empty?: boolean;
  [key: string]: any;
}

export type StatusColumnType = {
  bgColor: string;
  color: string;
  label: string;
};

export interface DataListField {
  id: number;
  name?: string;
  label?: string;
  typeId?: number;
  displayTypeId?: DisplayType;
  position?: number;
  priority?: ColumnPriorityType;
  width?: number;
  formattings?: KeyValueFormattingModel[];
  restrictions?: KeyValueRestrictionModel[];
  isFilter?: boolean;
  [key: string]: unknown;
}

export enum DisplayFormats {
  Text = 0,
  Status = 1,
  Date = 2,
  Number = 3,
  YesNo = 4,
  Image = 5,
  Froala = 6,
  Avatar = 7,
  /** Cell value is an i18n key resolved in the table cell template. */
  TranslateKey = 8,
  /** Cell value is an icon with optional text rendered before or after it. */
  IconText = 9,
}

export type IconTextCell = {
  iconKey: string;
  text?: string;
  title?: string;
  color?: string;
  iconClass?: string;
  iconPosition?: 'before' | 'after';
};

export interface CipoTableOptions<A = ActionType, T = TableRow> {
  selectable?: boolean;
  actions?: (row?: T) => A[];
  clickableRows?: boolean;
  pagination?: TablePaginator;
  hidePagesLoaded?: boolean;
  sort?: Sort;
  serverPaginationSort?: boolean;
  noDataPlaceholder?: string;
  emptyDataListText?: string;
}

export type TablePaginator = {
  pageSize?: number;
  pageSizeOptions?: number[];
  pageIndex?: number;
  length?: number;
  pagesCount?: number;
};

export type ActionSubItem<T = TableRow> = {
  displayName: string;
  leftIcon?: string;
  onSelect?: (row: T) => void;
  subItems?: ActionSubItem<T>[];
};

export type ActionType<E = number, T = TableRow> = {
  id: E;
  displayName: string;
  leftIcon?: string;
  rightIcon?: string;
  condition?: (row: T) => boolean;
  handleActionExternally?: boolean;
  subItems?: ActionSubItem<T>[];
};

export type ActionEventType<I = number, T = TableRow | TableRow[]> = {
  actionId: I;
  data: T;
};

export function actionTypeFromDropdownItem<E = number, T = TableRow>(
  item: DropdownItem,
  iconPosition: 'left' | 'right' = 'left',
): ActionType<E, T> {
  return {
    id: item.id as E,
    displayName: item.label ?? '',
    [iconPosition === 'left' ? 'leftIcon' : 'rightIcon']: item.icon,
  };
}
